import { useParams } from "react-router";
import { Toast } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAppDispatch, useCurrentOrganization } from "Hooks";
import { apiErrorToast } from "Utils";
import {
  useGetOrganizationPolicyQuery,
  useUpdateOrganizationPolicyMutation,
} from "../services/policies.api";
import { resetConfiguration } from "../store/policyConfigSlice";
import {
  PolicyConfig,
  PolicyConfigurationScope,
  UpdateOrganizationRequestBody,
} from "../types";

interface IUseOrganizationConfig {
  configScope: PolicyConfigurationScope;
}

const useOrganizationConfig = ({
  configScope,
}: IUseOrganizationConfig): PolicyConfig => {
  const { organizationId } = useParams();
  const dispatch = useAppDispatch();
  const currentOrganization = useCurrentOrganization();

  const {
    isLoading,
    isFetching,
    isError,
    error,
    data: policies,
  } = useGetOrganizationPolicyQuery(
    { organizationId: organizationId ?? "" },
    { skip: !organizationId },
  );

  const status = getQueryStatus({
    isLoading,
    isFetching,
    isError,
    error,
    data: policies,
  });

  const [updateOrganization] = useUpdateOrganizationPolicyMutation();

  const updatePolicy = async (body: UpdateOrganizationRequestBody) => {
    try {
      await updateOrganization({
        organizationId: organizationId ?? "",
        body,
      }).unwrap();
      dispatch(resetConfiguration(organizationId ?? ""));

      Toast.show({
        title: "Policy configuration changes saved",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not save policy configuration changes");
    }
  };

  return {
    entityName: currentOrganization?.name ?? "",
    configScope,
    status,
    isError,
    updatePolicy,
    policies: policies?.policies ?? {},
    reprocessEstimate: policies?.estimate ?? 0,
    rlScanLevel: policies?.rl_scan_level ?? {
      inherited_level: null,
      current_level: null,
    },
    permissions: [PERMISSION.CONFIGURE_ORGANIZATION_POLICY_PROFILE],
  };
};

export default useOrganizationConfig;
